import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import consumer from "../channels/consumer";
import GoogleCampaignOverviewPanel from "../components/google-ads/GoogleCampaignOverviewPanel";
import GoogleAdsTargetingPanel from "../components/google-ads/GoogleAdsTargetingPanel";
import GoogleAdsCreatePanel from "../components/google-ads/GoogleAdsCreatePanel";
import GoogleCampaignPerformancePanel from "../components/google-ads/GoogleCampaignPerformancePanel";
import { formatStatusLabel } from "../components/google-ads/GoogleStatusBlock";
import ReportDisplay from "../components/google-ads/ReportDisplay";

const microsToDollars = (micros) =>
  micros == null ? "—" : `$${(Number(micros) / 1_000_000).toFixed(2)}`;

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "targeting", label: "Ad Group" },
  { id: "creative", label: "Google Ad" },
  { id: "performance", label: "Performance" },
  { id: "reports", label: "Marketing Plans" },
];

const EventGoogleAdsCampaignPage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");

  const companyId = useMemo(() => document.getElementById("react-root")?.getAttribute("company-id"), []);
  const isAdmin = useMemo(() => document.getElementById("react-root")?.getAttribute("is-admin") === "true", []);

  useEffect(() => {
    if (!campaignId) {
      navigate("/marketing/google_ads_ai");
      return;
    }
    fetch(`/api/google_ads/campaigns/${campaignId}`, {
      headers: { Accept: "application/json" },
    })
      .then((res) => {
        if (!res.ok) throw new Error("Campaign not found");
        return res.json();
      })
      .then((data) => {
        setCampaign(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load campaign");
        navigate("/marketing/google_ads_ai");
      });
  }, [campaignId, navigate]);

  const refetchCampaign = async () => {
    if (!campaignId) return;
    try {
      const res = await fetch(`/api/google_ads/campaigns/${campaignId}`, {
        headers: { Accept: "application/json" },
      });
      const data = await res.json();
      if (res.ok && data) setCampaign(data);
    } catch (e) {
      console.error("Failed to refetch campaign", e);
    }
  };

  const refetchCampaignRef = useRef(refetchCampaign);
  refetchCampaignRef.current = refetchCampaign;

  // ActionCable subscription for ad publish progress/errors (same channel as PublishGoogleAdJob broadcasts)
  useEffect(() => {
    if (!consumer || !companyId) return;
    const alertConfig = {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      theme: "light",
    };
    const subscription = consumer.subscriptions.create(
      { channel: "CompanyPostsChannel", company_id: companyId },
      {
        received(data) {
          try {
            if (data?.action === "ad_publish_progress") {
              toast.info(data.message || "Publishing...", { ...alertConfig, autoClose: 3000 });
              refetchCampaignRef.current?.();
            }
            if (data?.action === "ad_publish_error") {
              toast.error(data.message || "Failed to publish ad", alertConfig);
              refetchCampaignRef.current?.();
            }
          } catch (err) {
            console.error("[ActionCable] Error handling received data:", err);
          }
        },
      }
    );
    return () => subscription?.unsubscribe();
  }, [companyId]);

  const eventId = campaign?.event_id;
  const eventName = campaign?.event_name || "Event";
  const campaignName = campaign?.name || "Campaign";
  const adGroups = campaign?.ad_groups || [];
  const adsCount = adGroups.reduce((sum, ag) => sum + (ag.ads?.length || 0), 0);
  const isDraft = !campaign?.google_campaign_id && (campaign?.status || "").toLowerCase() === "draft";
  const reportId = campaign?.google_ads_ai_report_id ?? null;

  if (loading) {
    return (
      <div className="campaign-detail-page">
        <div className="campaign-detail-page__inner">
          <div className="campaign-detail-page__loading">
            <p>Loading campaign...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="campaign-detail-page">
        <div className="campaign-detail-page__inner">
          <div className="campaign-detail-page__not-found">
            <p>Campaign not found</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="campaign-detail-page">
        <div className="campaign-detail-page__inner">
          <div className="campaign-detail-page__section">
            <div className="campaign-detail-page__header-row">
              <div>
                <nav className="campaign-detail-page__breadcrumb" aria-label="Breadcrumb">
                  <Link to="/marketing/google_ads_ai" className="campaign-detail-page__breadcrumb-link">
                    Events
                  </Link>
                  <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  {eventId != null ? (
                    <>
                      <Link
                        to="/marketing/google_ads_ai"
                        state={{ selectedEventId: eventId }}
                        className="campaign-detail-page__breadcrumb-link"
                        title={eventName}
                      >
                        {eventName.length > 50 ? eventName.slice(0, 50) + "..." : eventName}
                      </Link>
                      <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                    </>
                  ) : null}
                  <span className="campaign-detail-page__breadcrumb-text">Campaigns</span>
                  <span className="campaign-detail-page__breadcrumb-sep" aria-hidden="true"> &gt; </span>
                  <span className="campaign-detail-page__breadcrumb-text campaign-detail-page__breadcrumb-current" title={campaignName}>
                    {campaignName.length > 50 ? campaignName.slice(0, 50) + "..." : campaignName}
                  </span>
                </nav>
                {isDraft && (
                  <span className="campaign-detail-page__badge">DRAFT</span>
                )}
                <h1 className="campaign-detail-page__title">{campaignName}</h1>
                {eventName && eventName !== "Event" && (
                  <p className="campaign-detail-page__subtitle">{eventName}</p>
                )}
              </div>
            </div>

            <div className="campaign-detail-page__strategy-card">
              <h3 className="campaign-detail-page__strategy-title">STRATEGY SUMMARY</h3>
              <div className="campaign-detail-page__strategy-grid">
                <div>
                  <div className="campaign-detail-page__strategy-label">BUDGET</div>
                  <div className="campaign-detail-page__strategy-value">
                    {microsToDollars(campaign.amount_micros)}
                  </div>
                  <div className="campaign-detail-page__strategy-note">{campaign.budget_name || "—"}</div>
                </div>
                <div>
                  <div className="campaign-detail-page__strategy-label">STRUCTURE</div>
                  <div className="campaign-detail-page__strategy-value">
                    1 / {adGroups.length} / {adsCount}
                  </div>
                  <div className="campaign-detail-page__strategy-note">campaign / ad groups / ads</div>
                </div>
                <div>
                  <div className="campaign-detail-page__strategy-label">STATUS</div>
                  <div className="campaign-detail-page__strategy-value">
                    {formatStatusLabel(campaign.status || "draft")}
                  </div>
                </div>
              </div>
            </div>

            <div className="campaign-detail-page__tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  className={`campaign-detail-page__tab ${activeTab === tab.id ? "campaign-detail-page__tab--active" : ""}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div className="campaign-detail-page__content-card">
            {activeTab === "overview" && (
              <GoogleCampaignOverviewPanel
                campaign={campaign}
                onSave={setCampaign}
                onRefetch={refetchCampaign}
                toast={toast}
              />
            )}
            {activeTab === "targeting" && (
              <GoogleAdsTargetingPanel
                campaign={campaign}
                onRefetch={refetchCampaign}
                toast={toast}
              />
            )}
            {activeTab === "creative" && (
              <GoogleAdsCreatePanel
                campaign={campaign}
                onRefetch={refetchCampaign}
                toast={toast}
              />
            )}
            {activeTab === "performance" && (
              <GoogleCampaignPerformancePanel
                campaign={campaign}
                onRefetch={refetchCampaign}
                toast={toast}
              />
            )}
            {activeTab === "reports" && (
              reportId ? (
                <ReportDisplay reportId={reportId} onClose={null} isAdmin={isAdmin} />
              ) : (
                <div className="campaign-detail-page__reports-empty">
                  <p>No report data available for this campaign.</p>
                  <p>This campaign was not generated from an AI report.</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
  );
};

export default EventGoogleAdsCampaignPage;
